import produce from 'immer';
import { create } from 'zustand';
import { getOrders, setSapNumber, setCancelOrder, editOrder, uploadCEC, deleteCEC } from '../../../services/orders/ordersService';
import { OrdersDataSource } from '../../../datasources/orders/OrdersDataSource';
import { formatDate } from '../../../utils/date';
import { PatientDataSource } from '../../../datasources/patients/PatientDataSource';
import { CecDataSource } from '../../../datasources/orders/CecDataSource';

// Extrai o CNPJ puro (apenas dígitos) do label "Empresa A - 12.345.678/0001-00"
const extractCnpjFromEmpresaCnpjFilter = (value) => {
  if (!value) return undefined;
  const parts = String(value).split(' - ');
  const cnpjPart = parts[parts.length - 1];
  const digits = cnpjPart.replace(/\D/g, '');
  return digits || undefined;
};

const uniqueValues = (values) => {
  return Array.from(
    new Set(
      values
        .map((value) => (value == null ? '' : String(value).trim()))
        .filter((value) => value !== '')
    )
  );
};

const getFilterValues = (filter, name) => {
  return uniqueValues((filter ?? []).filter((item) => item?.name === name).map((item) => item?.value));
};

const getFirstFilterValue = (filter, name) => {
  return (filter ?? []).find((item) => item?.name === name)?.value;
};

const getDateRanges = (filter, name) => {
  return (filter ?? [])
    .filter((item) => item?.name === name && Array.isArray(item?.value))
    .map((item) => item.value)
    .filter((range) => range[0] && range[1]);
};

const getOrderDateRangesParam = (filter) => {
  return getDateRanges(filter, 'Data').map((range) => ({
    initial_date: formatDate(range[0]),
    final_date: formatDate(range[1]),
  }));
};

const getDeliveryDateRangesParam = (filter) => {
  return getDateRanges(filter, 'Estimativa de entrega').map((range) => ({
    initial_delivery_date: formatDate(range[0]),
    final_delivery_date: formatDate(range[1]),
  }));
};

const asSingleOrArray = (values) => {
  if (!values.length) return undefined;
  return values.length === 1 ? values[0] : values;
};

const getFilterParam = (filter, name) => asSingleOrArray(getFilterValues(filter, name));

const normalizeCompanyTypeFilterValue = (value) => {
  const text = String(value ?? '').trim().toLowerCase();
  if (text === 'público' || text === 'publico' || text === 'public') return 'public';
  if (text === 'privado' || text === 'private') return 'private';
  return '';
};

const getCompanyTypeParam = (filter) => {
  return asSingleOrArray(
    uniqueValues(
      getFilterValues(filter, 'Tipo de empresa')
        .map((value) => normalizeCompanyTypeFilterValue(value))
        .filter(Boolean)
    )
  );
};

const getEmpresaCnpjParam = (filter) => {
  const cnpjs = uniqueValues(
    getFilterValues(filter, 'Empresa - CNPJ')
      .map((value) => extractCnpjFromEmpresaCnpjFilter(value))
      .filter(Boolean)
  );

  return asSingleOrArray(cnpjs);
};

export const useOrderStore = create((set, get) => ({
  rows: 0,
  page: 0,
  pageSize: 10,
  orders: [],
  setPagination: (page, pageSize) => {
    set({ page, pageSize });
  },
  fetchOrders: async (cnpj, filter) => {
    const orderDateRange = getFirstFilterValue(filter, 'Data');
    const deliveryDateRange = getFirstFilterValue(filter, 'Estimativa de entrega');
    const orderDateRanges = getOrderDateRangesParam(filter);
    const deliveryDateRanges = getDeliveryDateRangesParam(filter);

    const params = {
      page: parseInt(get().page),
      page_size: parseInt(get().pageSize),
      number: getFilterParam(filter, 'Nº Pedido'),
      type: getFilterParam(filter, 'Tipo'),
      status: getFilterParam(filter, 'Status'),
      cpf: getFilterParam(filter, 'CPF'),
      cnpj: getEmpresaCnpjParam(filter),
      company_type: getCompanyTypeParam(filter),
      patient_name: getFilterParam(filter, 'Paciente'),
      initial_date: Array.isArray(orderDateRange) ? formatDate(orderDateRange[0]) : undefined,
      final_date: Array.isArray(orderDateRange) ? formatDate(orderDateRange[1]) : undefined,
      date_ranges: orderDateRanges.length > 1 ? orderDateRanges : undefined,
      order_sap_number: getFilterParam(filter, 'Nº Pedido SAP'),
      client_sap_number: getFilterParam(filter, 'Nº Cliente SAP'),
      initial_delivery_date: Array.isArray(deliveryDateRange) ? formatDate(deliveryDateRange[0]) : undefined,
      final_delivery_date: Array.isArray(deliveryDateRange) ? formatDate(deliveryDateRange[1]) : undefined,
      delivery_date_ranges: deliveryDateRanges.length > 1 ? deliveryDateRanges : undefined,
      client: undefined,
      alarm: getFilterParam(filter, 'Sinal luminoso'),
      operator: getFilterParam(filter, 'Operador Responsável'),
      distribution_center: getFilterParam(filter, 'Filial Distribuidora'),
      _timestamp: new Date().getTime(),
    };

    const value = await new OrdersDataSource().getAll(params);

    set(
      produce((state) => {
        state.orders = [...value.data];
        state.rows = value.total;
      })
    );
  },
  uploadCEC: async (CECs) => {
    await uploadCEC(CECs);
  },
  setSapNumber: async (cnpj, orderNumber, sapNumber) => {
    await setSapNumber(cnpj, orderNumber, sapNumber);
  },
  setCancelOrder: async (cnpj, orderNumber, orderStatus) => {
    await setCancelOrder(cnpj, orderNumber, orderStatus);
  },
  editOrder: async (cnpj, orderToUpdate) => {
    await editOrder(cnpj, orderToUpdate);
  },
  deleteCEC: async (sap_number) => {
    await deleteCEC(sap_number);
  },
}));

function updateAlarmById(order_, id, newAlarm) {
  return {
    ...order_,
    data: order_.data.map((item) => (item.id === id ? { ...item, alarm: newAlarm } : item)),
  };
}
